import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Plus, FileText, Calculator, CheckCircle2, AlertCircle, Loader2, ChevronDown, MapPin, RefreshCw, ExternalLink, User } from 'lucide-react';
import { supabase } from '../../../../src/lib/supabase';
import { useAuth } from '../../../../src/contexts/AuthContext';
import { logUsageEvent } from '../../../../src/lib/logger';
import { UnidadeConsumidora, Negociacao } from '../../../../types';
import UnidadeModal from '../../../Financeiro/UnidadeModal'; // Adjust import path as needed
import { useToast } from '../../../../src/contexts/ToastContext';
import { getEnderecosTitularLemit, EnderecoTitular, enderecoKey, formatCep } from '../../../../src/services/lemit';
import { sanitizeNomeParaArquivo } from '../../../../src/lib/utils';
import { propsFundoModal } from '../../../../src/lib/backdropModal';
import { numeroWhatsappBr } from '../../../../src/lib/enderecoUc';
import { modelosDoTermo, ucDaVigor, type ModeloCobranca } from '../../../../src/lib/boletoUnico/modeloCobranca';
import { erroContatosDoTermo, financeiroEhOutraPessoa } from '../../../../src/lib/pagadorUc';
import { logNegotiationAction } from '../../../../src/lib/negotiationLogger';
import { formatarDocumento } from '../../../../src/lib/consultaDocumento';

export type SimulacaoTermo = NonNullable<Negociacao['url_simulacao']>[number];

// Seção "Confira o cadastro e os contatos" do termo (#6384): mesmos campos do
// PerguntaPagador / Editar cliente (foco primary, placeholder, WhatsApp mascarado).
const inputContatoCls = 'w-full border border-gray-200 dark:border-slate-700 rounded-xl px-3 py-2 text-sm outline-none transition-all focus:border-primary focus:ring-2 focus:ring-primary/20 bg-white dark:bg-slate-900 text-gray-800 dark:text-slate-100 placeholder:text-gray-400 dark:placeholder:text-slate-500 disabled:opacity-60 disabled:cursor-not-allowed';
const labelContatoCls = 'block text-[11px] font-bold uppercase text-gray-700 dark:text-slate-300 mb-1';
const legendaContatoCls = 'block text-[11px] font-bold uppercase text-gray-500 dark:text-slate-400 mb-1';
const obrigatorio = <span className="text-red-500" aria-hidden="true">*</span>;

/** (62) 99999-8888 — aceita colar "+55 62 99999-8888" sem perder o DDD. */
const mascaraFoneContato = (v: string) => {
    let d = v.replace(/\D/g, '');
    if (d.length > 11 && d.startsWith('55')) d = d.slice(2);
    d = d.slice(0, 11);
    if (d.length <= 2) return d;
    if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
    if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
    return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
};

/** Modelo de cobrança das UCs do termo (#6367). Desde 03/11/2026: sempre 'unificado', assinatura vale como aceite. */
export interface CobrancaDoTermo {
    modelos: Record<number, ModeloCobranca | undefined>;
    aceitePelaAssinatura: boolean;
}

interface SimulationModalProps {
    isOpen: boolean;
    onClose: () => void;
    negotiationId: number | string;
    clientName: string;
    ucs: UnidadeConsumidora[];
    onUpdate: () => void; // To refresh UCs list after adding a new one
    onSubmit?: (selectedUcs: UnidadeConsumidora[], enderecoTitular?: EnderecoTitular | null, simulacao?: SimulacaoTermo | null, cobranca?: CobrancaDoTermo) => Promise<boolean | void>; // To handle generation
    mode?: 'simulation' | 'term';
    cpfCnpj?: string; // Termo: documento do contratante para buscar o endereço na Lemit
    simulations?: Negociacao['url_simulacao']; // Termo: simulações da negociação — usuário escolhe qual anexar ao Quadro Resumo
}

const SimulationModal: React.FC<SimulationModalProps> = ({
    isOpen,
    onClose,
    negotiationId,
    clientName,
    ucs,
    onUpdate,
    onSubmit,
    mode = 'simulation',
    cpfCnpj,
    simulations
}) => {

    const isTerm = mode === 'term';
    const { addToast } = useToast();
    const { user } = useAuth();

    // Termo (#6384): os contatos saíram da Nova Negociação e são exigidos aqui. O
    // vendedor confere o cadastro, informa o WhatsApp do associado e diz se o contato
    // financeiro é outra pessoa; se for, nome e WhatsApp dela, com número diferente.
    // O contato financeiro não muda em nome de quem sai o boleto.
    const [contatos, setContatos] = useState<{
        cliente_nome: string; cpf_cnpj: string; whatsapp: string; email: string;
        financeiro_outro: boolean | null; contato_financeiro: string; whatsapp_financeiro: string; email_financeiro: string;
    } | null>(null);
    const [contatosErro, setContatosErro] = useState(false);
    // Erro de validação dos contatos: marca o campo, mostra a mensagem junto dele e leva o foco até lá.
    const [erroContato, setErroContato] = useState<{ campo: string; mensagem: string } | null>(null);

    // Termo: endereço do contratante que sai no Quadro Resumo — o usuário escolhe
    // entre os endereços cadastrais da Lemit e os endereços das UCs da negociação
    const [lemitEnderecos, setLemitEnderecos] = useState<EnderecoTitular[]>([]);
    const [enderecoLoading, setEnderecoLoading] = useState(false);
    const [selectedEnderecoKey, setSelectedEnderecoKey] = useState<string | null>(null);

    // Termo: simulação (Plano de Benefícios) anexada ao Quadro Resumo — mais recente primeiro
    const simulacaoOpcoes = React.useMemo(() => (isTerm ? [...(simulations || [])].reverse() : []), [isTerm, simulations]);
    const [selectedSimulacaoIdx, setSelectedSimulacaoIdx] = useState(0);

    const [selectedIds, setSelectedIds] = useState<number[]>([]);
    const [isUnidadeModalOpen, setIsUnidadeModalOpen] = useState(false);
    const [showAddOptions, setShowAddOptions] = useState(false);
    const [loading, setLoading] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [countdown, setCountdown] = useState(8);

    // Track previous UCs to identify new ones for auto-selection
    const [prevUcsLength, setPrevUcsLength] = useState(0);

    // Countdown effect
    useEffect(() => {
        let interval: NodeJS.Timeout;
        if (isProcessing && countdown > 0) {
            interval = setInterval(() => {
                setCountdown(prev => prev - 1);
            }, 1000);
        } else if (isProcessing && countdown === 0) {
            // Finished
            if (onUpdate) onUpdate();
            setIsProcessing(false);
            onClose();
        }
        return () => clearInterval(interval);
    }, [isProcessing, countdown, onUpdate, onClose]);

    // Reset countdown on open
    useEffect(() => {
        if (isOpen) {
            setSelectedIds(ucs.map(uc => uc.id));
            setCountdown(8);
            setIsProcessing(false);
            setSelectedSimulacaoIdx(0); // padrão: simulação mais recente
        }
    }, [isOpen]);

    // Esc fecha a janela (menos durante a geração ou com o cadastro de UC aberto por cima)
    useEffect(() => {
        if (!isOpen || isProcessing || loading || isUnidadeModalOpen) return;
        const aoTeclar = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', aoTeclar);
        return () => document.removeEventListener('keydown', aoTeclar);
    }, [isOpen, isProcessing, loading, isUnidadeModalOpen, onClose]);

    // Mexeu em qualquer contato: a marca de erro anterior não vale mais
    useEffect(() => { setErroContato(null); }, [contatos]);

    // Termo: carrega o cadastro e os contatos já gravados
    useEffect(() => {
        if (!isOpen || !isTerm) return;
        let vivo = true;
        setContatos(null);
        setContatosErro(false);
        (async () => {
            const { data, error } = await supabase
                .from('negociacoes')
                .select('cliente_nome, cpf_cnpj, whatsapp, email, contato_financeiro, whatsapp_financeiro, email_financeiro')
                .eq('id', negotiationId)
                .maybeSingle();
            if (!vivo) return;
            // Sem o cadastro não dá para conferir nem gravar: o botão fica travado.
            if (error || !data) {
                setContatosErro(true);
                addToast('Não foi possível carregar o cadastro. Feche e abra o termo de novo.', 'error');
                return;
            }
            const n: any = data;
            // Financeiro com nome, e-mail ou WhatsApp próprio abre como "outra pessoa",
            // já preenchido; o copiado do cliente pede a resposta do vendedor.
            const outro = financeiroEhOutraPessoa(n);
            setContatos({
                cliente_nome: n.cliente_nome || clientName, cpf_cnpj: n.cpf_cnpj || '', whatsapp: n.whatsapp || '', email: n.email || '',
                financeiro_outro: outro ? true : null,
                contato_financeiro: outro ? (n.contato_financeiro || '') : '',
                whatsapp_financeiro: outro ? (n.whatsapp_financeiro || '') : '',
                email_financeiro: outro ? (n.email_financeiro || '') : '',
            });
        })();
        return () => { vivo = false; };
    }, [isOpen, isTerm, negotiationId]);

    /** Marca o campo do erro, avisa e leva o foco até ele (o aviso só em toast ficava longe do campo). */
    const avisarErroContato = (mensagem: string) => {
        const campo = /^Responda/.test(mensagem) ? 'termo-fin-nao'
            : /financeiro/i.test(mensagem) && /nome/i.test(mensagem) ? 'termo-fin-nome'
            : /financeiro/i.test(mensagem) && /e-?mail/i.test(mensagem) ? 'termo-fin-email'
            : /financeiro/i.test(mensagem) ? 'termo-fin-whats'
            : /e-?mail/i.test(mensagem) ? 'termo-email'
            : 'termo-whatsapp';
        setErroContato({ campo, mensagem });
        addToast(mensagem, 'warning');
        setTimeout(() => document.getElementById(campo)?.focus(), 0);
    };

    /** Grava os contatos antes do termo sair. false = parou (o aviso já foi dado). */
    const salvarContatosDoTermo = async (): Promise<boolean> => {
        if (!contatos) return false;
        // A Autentique recusa o documento com e-mail malformado: barra aqui, não depois do termo gerado.
        const emailOk = (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v.trim());
        const erro = erroContatosDoTermo(contatos)
            ?? (!emailOk(contatos.email) ? 'O e-mail do associado não parece válido. Confira se tem @ e o domínio.' : null)
            ?? (contatos.financeiro_outro === true && contatos.email_financeiro.trim() && !emailOk(contatos.email_financeiro)
                ? 'O e-mail do financeiro não parece válido. Confira o @ e o domínio, ou deixe em branco.' : null);
        if (erro) {
            avisarErroContato(erro);
            return false;
        }
        const outro = contatos.financeiro_outro === true;
        const novos = {
            whatsapp: contatos.whatsapp,
            email: contatos.email.trim(),
            // Quadro Resumo do termo: vazio = o n8n usa o dado principal do associado.
            contato_financeiro: outro ? contatos.contato_financeiro.trim() : null,
            whatsapp_financeiro: outro ? contatos.whatsapp_financeiro : null,
            email_financeiro: outro ? (contatos.email_financeiro || null) : null,
        };
        // .select('id'): se a permissão do banco barrar o UPDATE, ele volta sem erro e sem linha gravada
        const { data: gravados, error } = await supabase.from('negociacoes').update(novos).eq('id', negotiationId).select('id');
        if (error || !gravados?.length) {
            console.error('Termo: contatos não gravados', error);
            addToast('Não foi possível salvar os contatos. Tente de novo; se continuar, avise o suporte.', 'error');
            return false;
        }
        await logNegotiationAction(Number(negotiationId), user?.id, 'conferiu os contatos no termo', {
            financeiro_outra_pessoa: outro,
        });
        return true;
    };

    // Termo: busca os endereços do contratante na Lemit ao abrir (cache-first, sem custo)
    useEffect(() => {
        if (!isOpen || !isTerm) return;
        let active = true;
        setLemitEnderecos([]);
        setSelectedEnderecoKey(null);
        setEnderecoLoading(true);
        getEnderecosTitularLemit(cpfCnpj || '', Number(negotiationId))
            .then(ends => { if (active) setLemitEnderecos(ends); })
            .catch(err => console.warn('Lemit endereço do titular:', err?.message || err))
            .finally(() => { if (active) setEnderecoLoading(false); });
        return () => { active = false; };
    }, [isOpen, isTerm, cpfCnpj, negotiationId]);

    // Re-consulta forçada na Lemit (gera custo — ação explícita do usuário)
    const handleRefreshEndereco = async () => {
        setEnderecoLoading(true);
        try {
            const ends = await getEnderecosTitularLemit(cpfCnpj || '', Number(negotiationId), true);
            setLemitEnderecos(ends);
            if (ends.length === 0) addToast('A Lemit ainda não tem endereço cadastrado para este CPF/CNPJ.', 'warning');
        } catch (err: any) {
            addToast('Consulta Lemit falhou: ' + (err?.message || 'Erro desconhecido'), 'error');
        } finally {
            setEnderecoLoading(false);
        }
    };

    // Endereço vindo do cadastro da UC, no mesmo formato do endereço Lemit
    const enderecoFromUC = (uc: UnidadeConsumidora): EnderecoTitular | null => {
        const rua = (uc.logradouro || '').trim();
        if (!rua) return null;
        return {
            rua,
            numero: (uc.numero || '').trim(),
            complemento: (uc.complemento || '').trim(),
            quadra: (uc.quadra || '').trim(),
            lote: (uc.lote || '').trim(),
            bairro: (uc.bairro || '').trim(),
            cidade: (uc.cidade || '').trim(),
            estado: (uc.estado || '').trim(),
            cep: formatCep(uc.cep),
            fonte: 'uc',
        };
    };

    // Opções de endereço do contratante: todos os da Lemit + os das UCs (sem repetir)
    const enderecoOpcoes = React.useMemo(() => {
        if (!isTerm) return [] as { key: string; origem: 'lemit' | 'uc'; ucNumero?: string; endereco: EnderecoTitular }[];
        const opcoes: { key: string; origem: 'lemit' | 'uc'; ucNumero?: string; endereco: EnderecoTitular }[] = [];
        const seen = new Set<string>();
        lemitEnderecos.forEach(end => {
            const key = enderecoKey(end);
            if (seen.has(key)) return;
            seen.add(key);
            opcoes.push({ key: `lemit-${key}`, origem: 'lemit', endereco: end });
        });
        ucs.forEach(uc => {
            const end = enderecoFromUC(uc);
            if (!end) return;
            const key = enderecoKey(end);
            if (seen.has(key)) return; // mesmo endereço já oferecido pela Lemit
            seen.add(key);
            opcoes.push({ key: `uc-${uc.id}`, origem: 'uc', ucNumero: uc.numero_uc, endereco: end });
        });
        return opcoes;
    }, [isTerm, lemitEnderecos, ucs]);

    // Mantém uma opção sempre marcada (padrão: melhor endereço da Lemit).
    // Espera o carregamento da Lemit p/ não auto-selecionar uma UC antes de a Lemit chegar.
    useEffect(() => {
        if (!isTerm || enderecoLoading) return;
        if (selectedEnderecoKey && enderecoOpcoes.some(o => o.key === selectedEnderecoKey)) return;
        setSelectedEnderecoKey(enderecoOpcoes[0]?.key ?? null);
    }, [isTerm, enderecoLoading, enderecoOpcoes, selectedEnderecoKey]);

    // ... (Use existing auto-select logic) ...
    // Better tracking for auto-selection
    const prevUcsRef = React.useRef<UnidadeConsumidora[]>(ucs);
    useEffect(() => {
        const newUcs = ucs.filter(uc => !prevUcsRef.current.find(p => p.id === uc.id));
        if (newUcs.length > 0) {
            setSelectedIds(prev => [...prev, ...newUcs.map(u => u.id)]);
        }
        prevUcsRef.current = ucs;
    }, [ucs]);


    if (!isOpen) return null;

    const ucsDoTermo = ucs.filter(uc => selectedIds.includes(uc.id));

    // Por que o botão "Gerar termo" está travado (null = liberado). Aparece no rodapé e no title.
    const motivoTravado: string | null = loading ? null
        : selectedIds.length === 0 ? 'Selecione pelo menos uma UC.'
        : !isTerm ? null
        : contatosErro ? 'O cadastro não carregou. Feche e abra o termo de novo.'
        : !contatos ? 'Carregando o cadastro...'
        : enderecoLoading ? 'Buscando os endereços na Lemit...'
        : simulacaoOpcoes.length === 0 ? 'Gere a simulação de economia antes de emitir o termo.'
        : null;

    // Campo de contato com erro: borda vermelha e ligação com a mensagem para leitor de tela
    const campoContato = (id: string) => erroContato?.campo === id
        ? { className: `${inputContatoCls} !border-red-400 dark:!border-red-500`, 'aria-invalid': true as const, 'aria-describedby': 'termo-contatos-erro' }
        : { className: inputContatoCls };

    const toggleSelect = (id: number) => {
        if (isProcessing) return;
        setSelectedIds(prev =>
            prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
        );
    };

    const handleGenerateSimulation = async () => {
        setLoading(true);
        try {
            // ... (keep existing fetch logic) ...
            // 1. Get Webhook URL, Negotiation Details, and Fresh UCs
            const [{ data: settings }, { data: negotiationData }, { data: freshUcs }] = await Promise.all([
                supabase.from('system_settings').select('value').eq('key', 'simulation_webhook_url').single(),
                supabase.from('negociacoes').select('pasta_sharepoint_url, responsavel_id, cpf_cnpj, fidelidade, cliente_nome, whatsapp, email, titulo').eq('id', negotiationId).single(),
                supabase.from('unidades_consumidoras').select('*').eq('negociacao_id', negotiationId)
            ]);

            if (!settings?.value) {
                addToast('URL de simulação não configurada. Por favor, configure em Configurações > APIs.', 'error');
                setLoading(false);
                return;
            }

            // Fetch Responsible Details if ID exists
            let responsavelInfo = { nome: '', telefone: '', email: '' };
            if (negotiationData?.responsavel_id) {
                // Fetch profile
                const { data: profile } = await supabase
                    .from('profiles')
                    .select('full_name, whatsapp')
                    .eq('id', negotiationData.responsavel_id)
                    .single();

                // Fetch email via RPC
                const { data: email } = await supabase.rpc('get_user_email', { user_id: negotiationData.responsavel_id });

                responsavelInfo = {
                    nome: profile?.full_name || '',
                    // Vai direto para o link wa.me do botão "Solicitar adesão via WhatsApp" do
                    // PDF (único uso no template do n8n): só dígitos com 55 (#6279).
                    telefone: numeroWhatsappBr(profile?.whatsapp),
                    email: email || ''
                };
            }

            // 2. Filter UCs (using fresh data)
            const ucsToUse = freshUcs || ucs;
            const selectedUCsList = ucsToUse.filter(uc => selectedIds.includes(uc.id));

            if (selectedUCsList.length === 0) {
                addToast('Selecione pelo menos uma UC.', 'warning');
                setLoading(false);
                return;
            }

            if (mode === 'term' && onSubmit) {
                // For Term mode, let the parent handle the Webhook logic with the selected UCs
                // (o endereço escolhido pelo usuário vai junto p/ não pagar 2ª consulta Lemit)
                const enderecoEscolhido = enderecoOpcoes.find(o => o.key === selectedEnderecoKey)?.endereco ?? null;
                const simulacaoEscolhida = simulacaoOpcoes[selectedSimulacaoIdx] ?? null;
                if (!(await salvarContatosDoTermo())) {
                    setLoading(false);
                    return;
                }
                // Todo cliente novo é boleto único (03/11/2026): sem escolha, e a assinatura vale como aceite.
                const modelos = modelosDoTermo(selectedUCsList);
                const result = await onSubmit(selectedUCsList, enderecoEscolhido, simulacaoEscolhida, {
                    modelos,
                    aceitePelaAssinatura: Object.keys(modelos).length > 0,
                });
                if (result !== false) {
                    setIsProcessing(true); // start countdown only if it didn't fail validation
                }
                return;
            }

            // Generate Simulation Number
            const now = new Date();
            const year = now.getFullYear();
            const month = String(now.getMonth() + 1).padStart(2, '0');
            const day = String(now.getDate()).padStart(2, '0');
            const hours = String(now.getHours()).padStart(2, '0');
            const minutes = String(now.getMinutes()).padStart(2, '0');
            const seconds = String(now.getSeconds()).padStart(2, '0');
            const numeroSimulacao = `VGR-${year}-${month}${day}${hours}${minutes}${seconds}`;

            // 3. Build Payload
            const payload = {
                id: negotiationId,
                numero_simulacao: numeroSimulacao,
                titulo: negotiationData?.titulo || null,
                cliente_nome: negotiationData?.cliente_nome || null,
                cliente_nome_arquivo: sanitizeNomeParaArquivo(negotiationData?.cliente_nome || ''),
                whatsapp: negotiationData?.whatsapp || null,
                email: negotiationData?.email || null,
                pasta_sharepoint_url: negotiationData?.pasta_sharepoint_url || null,
                cpf_cnpj: negotiationData?.cpf_cnpj || null,
                fidelidade: negotiationData?.fidelidade || null,
                responsavel: responsavelInfo,
                desconto_tarifa: selectedUCsList.length > 0 ? selectedUCsList[0].desconto_tarifa : null,
                desconto_bandeira: selectedUCsList.length > 0 ? selectedUCsList[0].desconto_bandeira : null,
                carencia: selectedUCsList.length > 0 ? selectedUCsList[0].carencia : null,
                ucs: selectedUCsList.map(uc => ({
                    numero_uc: uc.numero_uc,
                    nome_cliente: uc.nome_cliente,
                    cidade: uc.cidade,
                    estado: uc.estado,
                    desconto_tarifa: uc.desconto_tarifa,
                    desconto_bandeira: uc.desconto_bandeira,
                    carencia: uc.carencia,
                    consumo_medio_kwh: uc.consumo_medio_kwh,
                    valor_tarifa: uc.valor_tarifa,
                    tipo_ligacao: uc.tipo_ligacao,
                    grupo_tarifario: uc.grupo_tarifario,
                    concessionaria: uc.concessionaria
                }))
            };

            // 4. Send POST
            const response = await fetch(settings.value, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                // 5. Success
                logUsageEvent({ userId: user?.id, modulo: 'crm', funcionalidade: 'funil', acao: isTerm ? 'gerar_termo_adesao' : 'gerar_simulacao', origem: 'acao' });
                // Update negotiation tags to include "Simulação Gerada" if not present
                const { data: currentNeg } = await supabase
                    .from('negociacoes')
                    .select('tags')
                    .eq('id', negotiationId)
                    .single();

                const currentTags = currentNeg?.tags || [];
                if (!currentTags.includes('Simulação Gerada')) {
                    await supabase
                        .from('negociacoes')
                        .update({ tags: [...currentTags, 'Simulação Gerada'] })
                        .eq('id', negotiationId);
                }

                // START COUNTDOWN instead of closing
                setIsProcessing(true);
            } else {
                throw new Error('Erro ao enviar simulação: ' + response.statusText);
            }

        } catch (error: any) {
            console.error(error);
            addToast('Erro ao gerar simulação: ' + (error.message || 'Erro desconhecido'), 'error');
        } finally {
            setLoading(false);
        }
    };

    return createPortal(
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fade-in px-4" {...propsFundoModal(() => onClose())}>
            <div role="dialog" aria-modal="true" aria-label={isTerm ? 'Gerar termo de adesão' : 'Gerar simulação'} className="bg-white dark:bg-slate-900 rounded-2xl w-full max-w-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]" onClick={e => e.stopPropagation()}>

                {isProcessing ? (
                    <div className="flex flex-col items-center justify-center h-full p-12 text-center animate-fade-in">
                        <div className="w-20 h-20 bg-green-50 rounded-full flex items-center justify-center mb-6 relative">
                            <svg className="w-full h-full transform -rotate-90">
                                <circle
                                    cx="40"
                                    cy="40"
                                    r="36"
                                    stroke="currentColor"
                                    strokeWidth="8"
                                    fill="transparent"
                                    className="text-gray-100"
                                />
                                <circle
                                    cx="40"
                                    cy="40"
                                    r="36"
                                    stroke="currentColor"
                                    strokeWidth="8"
                                    fill="transparent"
                                    strokeDasharray={226}
                                    strokeDashoffset={226 - (226 * (8 - countdown)) / 8}
                                    className="text-green-500 transition-all duration-1000 ease-linear"
                                />
                            </svg>
                            <span className="absolute text-2xl font-bold text-green-600 font-mono">{countdown}s</span>
                        </div>
                        <h3 className="text-xl font-bold text-gray-800 mb-2">{isTerm ? 'Gerando Termo de Adesão...' : 'Processando Simulação...'}</h3>
                        <p className="text-gray-500 max-w-sm">{isTerm ? 'Estamos gerando o termo e preparando o documento para assinatura. Por favor, aguarde.' : 'Estamos gerando o PDF e atualizando os dados da proposta. Por favor, aguarde.'}</p>
                    </div>
                ) : (
                    <>
                        {/* Header */}
                        <div className="p-6 border-b border-gray-100 dark:border-slate-700/50 flex justify-between items-center bg-primary/5 dark:bg-primary/10">
                            <div>
                                <h3 className="font-poppins font-bold text-gray-800 dark:text-slate-100 text-lg">{isTerm ? 'Gerar termo de adesão' : 'Selecione as UCs'}</h3>
                                <p className="text-sm text-gray-500 dark:text-slate-400">{isTerm ? `Confira os contatos e escolha as UCs - ${clientName}` : `Para simulação de economia - ${clientName}`}</p>
                            </div>
                            <button onClick={onClose} aria-label="Fechar" className="p-2 hover:bg-white dark:hover:bg-slate-800 rounded-full transition-colors text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-300">
                                <X size={20} />
                            </button>
                        </div>

                        {/* Content */}
                        <div className="p-6 overflow-y-auto flex-1 custom-scrollbar">

                            {/* Termo (#6384): conferir o cadastro e os contatos que saem no Quadro Resumo */}
                            {isTerm && (
                                <div className="mb-4">
                                    <span className="flex items-center gap-1.5 text-[10px] font-extrabold uppercase text-gray-500 dark:text-slate-400 mb-2">
                                        <User size={12} />
                                        Confira o cadastro e os contatos
                                    </span>
                                    {contatosErro ? (
                                        <div className="px-4 py-3 bg-amber-50 dark:bg-amber-900/15 border border-amber-200/70 dark:border-amber-800/40 rounded-xl">
                                            <p className="flex items-center gap-1.5 text-xs text-amber-800 dark:text-amber-300">
                                                <AlertCircle size={12} className="flex-shrink-0" />
                                                Não foi possível carregar o cadastro. Feche e abra o termo de novo.
                                            </p>
                                        </div>
                                    ) : !contatos ? (
                                        <div className="flex items-center gap-2 px-4 py-3 bg-gray-50 dark:bg-slate-800/60 border border-gray-100 dark:border-slate-700/50 rounded-xl text-sm text-gray-500 dark:text-slate-400">
                                            <Loader2 size={14} className="animate-spin" />
                                            Carregando o cadastro...
                                        </div>
                                    ) : (
                                        <div className="space-y-3 px-4 py-3 rounded-xl border border-gray-100 dark:border-slate-700/50 bg-gray-50 dark:bg-slate-800/60">
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                                <div className="min-w-0">
                                                    <p className={legendaContatoCls}>Associado</p>
                                                    <p className="text-sm font-semibold text-gray-800 dark:text-slate-100 truncate" title={contatos.cliente_nome || undefined}>{contatos.cliente_nome || '—'}</p>
                                                </div>
                                                <div className="min-w-0">
                                                    <p className={legendaContatoCls}>CPF/CNPJ</p>
                                                    <p className="text-sm font-semibold text-gray-800 dark:text-slate-100 truncate">{contatos.cpf_cnpj ? formatarDocumento(contatos.cpf_cnpj) : '—'}</p>
                                                </div>
                                                <div>
                                                    <label htmlFor="termo-whatsapp" className={labelContatoCls}>WhatsApp do associado {obrigatorio}</label>
                                                    <input id="termo-whatsapp" type="tel" inputMode="tel" autoComplete="off" aria-required="true" value={contatos.whatsapp} disabled={loading} onChange={e => setContatos({ ...contatos, whatsapp: mascaraFoneContato(e.target.value) })} placeholder="(00) 00000-0000" {...campoContato('termo-whatsapp')} />
                                                </div>
                                                <div>
                                                    <label htmlFor="termo-email" className={labelContatoCls}>E-mail do associado {obrigatorio}</label>
                                                    <input id="termo-email" type="email" autoComplete="off" aria-required="true" value={contatos.email} disabled={loading} onChange={e => setContatos({ ...contatos, email: e.target.value })} placeholder="email@exemplo.com" {...campoContato('termo-email')} />
                                                </div>
                                            </div>
                                            <div>
                                                <p id="termo-financeiro-pergunta" className="text-xs font-bold text-gray-700 dark:text-slate-200 mb-2">O contato financeiro é outra pessoa? {obrigatorio}</p>
                                                <div role="radiogroup" aria-labelledby="termo-financeiro-pergunta" aria-required="true" className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                                                    {([[false, 'Não, é o associado'], [true, 'Sim, outra pessoa']] as const).map(([valor, rotulo]) => {
                                                        const ativo = contatos.financeiro_outro === valor;
                                                        return (
                                                            <button
                                                                key={rotulo}
                                                                id={valor ? undefined : 'termo-fin-nao'}
                                                                type="button"
                                                                role="radio"
                                                                aria-checked={ativo}
                                                                disabled={loading}
                                                                onClick={() => setContatos({ ...contatos, financeiro_outro: valor })}
                                                                className={`flex-1 rounded-xl border px-3 py-2 text-sm font-semibold transition-colors outline-none focus-visible:ring-2 focus-visible:ring-primary/30 disabled:opacity-60 disabled:cursor-not-allowed ${ativo
                                                                    ? 'border-primary bg-primary/10 text-primary dark:text-primary-light'
                                                                    : 'border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-gray-700 dark:text-slate-300 hover:border-primary/50'}`}
                                                            >
                                                                {rotulo}
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                            {contatos.financeiro_outro === true && (
                                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                                    <div>
                                                        <label htmlFor="termo-fin-nome" className={labelContatoCls}>Nome do contato financeiro {obrigatorio}</label>
                                                        <input id="termo-fin-nome" autoComplete="off" aria-required="true" value={contatos.contato_financeiro} disabled={loading} onChange={e => setContatos({ ...contatos, contato_financeiro: e.target.value })} placeholder="Quem cuida do pagamento" {...campoContato('termo-fin-nome')} />
                                                    </div>
                                                    <div>
                                                        <label htmlFor="termo-fin-whats" className={labelContatoCls}>WhatsApp do financeiro {obrigatorio}</label>
                                                        <input id="termo-fin-whats" type="tel" inputMode="tel" autoComplete="off" aria-required="true" value={contatos.whatsapp_financeiro} disabled={loading} onChange={e => setContatos({ ...contatos, whatsapp_financeiro: mascaraFoneContato(e.target.value) })} placeholder="diferente do associado" {...campoContato('termo-fin-whats')} />
                                                    </div>
                                                    <div>
                                                        <label htmlFor="termo-fin-email" className={labelContatoCls}>E-mail do financeiro</label>
                                                        <input id="termo-fin-email" type="email" autoComplete="off" value={contatos.email_financeiro} disabled={loading} onChange={e => setContatos({ ...contatos, email_financeiro: e.target.value })} placeholder="opcional" {...campoContato('termo-fin-email')} />
                                                    </div>
                                                </div>
                                            )}
                                            {erroContato && (
                                                <p id="termo-contatos-erro" role="alert" className="flex items-center gap-1.5 text-xs font-semibold text-red-600 dark:text-red-400">
                                                    <AlertCircle size={12} className="flex-shrink-0" />
                                                    {erroContato.mensagem}
                                                </p>
                                            )}
                                            <p className="text-[11px] text-gray-600 dark:text-slate-400">* obrigatório. Os contatos saem no Quadro Resumo do termo. O contato financeiro não muda quem paga o boleto. Para corrigir nome, CPF/CNPJ ou o pagador, use o lápis de Dados do Cliente (Editar cliente).</p>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Termo: simulação (Plano de Benefícios) anexada ao Quadro Resumo — usuário escolhe */}
                            {isTerm && (
                                <div className="mb-4">
                                    <span className="flex items-center gap-1.5 text-[10px] font-extrabold uppercase text-gray-500 dark:text-slate-400 mb-2">
                                        <Calculator size={12} />
                                        Simulação anexada ao termo
                                    </span>
                                    {simulacaoOpcoes.length > 0 ? (
                                        <div className="space-y-2">
                                            {simulacaoOpcoes.map((sim, idx) => {
                                                const selecionado = idx === selectedSimulacaoIdx;
                                                const dataStr = sim.data_criacao ? new Date(sim.data_criacao).toLocaleDateString('pt-BR') : '';
                                                return (
                                                    <div
                                                        key={`${sim.numero_simulacao || sim.url_pdf}-${idx}`}
                                                        onClick={() => !isProcessing && setSelectedSimulacaoIdx(idx)}
                                                        className={`
                                                            flex items-center gap-3 px-4 py-2.5 border rounded-xl cursor-pointer transition-all
                                                            ${selecionado
                                                                ? 'bg-green-50 dark:bg-green-900/15 border-green-300 dark:border-green-800/60'
                                                                : 'bg-white dark:bg-slate-900 border-gray-100 dark:border-slate-700/50 hover:border-green-300/60'}
                                                        `}
                                                    >
                                                        <div className={`flex-shrink-0 w-4 h-4 rounded-full border flex items-center justify-center transition-colors ${selecionado ? 'border-green-600 bg-green-600' : 'border-gray-300 dark:border-slate-600'}`}>
                                                            {selecionado && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <p className="text-sm font-semibold text-gray-800 dark:text-slate-100 truncate">
                                                                {sim.numero_simulacao || `Simulação de ${dataStr}`}
                                                            </p>
                                                            <p className="text-xs text-gray-500 dark:text-slate-400 truncate">
                                                                {[dataStr, sim.valor_economia && `Economia: ${sim.valor_economia}`].filter(Boolean).join(' · ')}
                                                            </p>
                                                        </div>
                                                        {idx === 0 && (
                                                            <span className="flex-shrink-0 text-[10px] font-bold px-2 py-0.5 rounded-full bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400">
                                                                Mais recente
                                                            </span>
                                                        )}
                                                        {sim.url_pdf && (
                                                            <a
                                                                href={sim.url_pdf}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                onClick={e => e.stopPropagation()}
                                                                className="flex-shrink-0 p-1.5 rounded-md text-gray-400 dark:text-slate-500 hover:text-primary hover:bg-primary/10 transition-colors"
                                                                title="Abrir PDF da simulação"
                                                            >
                                                                <ExternalLink size={14} />
                                                            </a>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ) : (
                                        <div className="px-4 py-3 bg-amber-50 dark:bg-amber-900/15 border border-amber-200/70 dark:border-amber-800/40 rounded-xl">
                                            <p className="flex items-center gap-1.5 text-xs text-amber-800 dark:text-amber-300">
                                                <AlertCircle size={12} className="flex-shrink-0" />
                                                Nenhuma simulação gerada — gere a simulação de economia antes de emitir o termo (o Quadro Resumo referencia o Plano de Benefícios).
                                            </p>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Termo: endereço do contratante que sai no Quadro Resumo — usuário escolhe */}
                            {isTerm && (
                                <div className="mb-4">
                                    <div className="flex items-center justify-between gap-2 mb-2">
                                        <span className="flex items-center gap-1.5 text-[10px] font-extrabold uppercase text-gray-500 dark:text-slate-400">
                                            <MapPin size={12} />
                                            Endereço do contratante no termo
                                        </span>
                                        <button
                                            onClick={handleRefreshEndereco}
                                            disabled={enderecoLoading}
                                            className="flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-bold text-gray-500 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors disabled:opacity-50"
                                            title="Consultar novamente na Lemit (gera custo)"
                                        >
                                            <RefreshCw size={12} className={enderecoLoading ? 'animate-spin' : ''} />
                                            Lemit
                                        </button>
                                    </div>

                                    {enderecoLoading ? (
                                        <div className="flex items-center gap-2 px-4 py-3 bg-gray-50 dark:bg-slate-800/60 border border-gray-100 dark:border-slate-700/50 rounded-xl text-sm text-gray-500 dark:text-slate-400">
                                            <Loader2 size={14} className="animate-spin" />
                                            Buscando endereços do titular na Lemit...
                                        </div>
                                    ) : enderecoOpcoes.length > 0 ? (
                                        <div className="space-y-2">
                                            {enderecoOpcoes.map(opcao => {
                                                const selecionado = opcao.key === selectedEnderecoKey;
                                                const end = opcao.endereco;
                                                // Quadra/lote só existem em endereços de UC — na Lemit já vêm embutidos no complemento
                                                const quadraLote = [
                                                    end.quadra && end.quadra !== 'Sem quadra' ? `Q ${end.quadra}` : null,
                                                    end.lote && end.lote !== 'Sem lote' ? `LT ${end.lote}` : null,
                                                ].filter(Boolean).join(' ');
                                                return (
                                                    <div
                                                        key={opcao.key}
                                                        onClick={() => !isProcessing && setSelectedEnderecoKey(opcao.key)}
                                                        className={`
                                                            flex items-start gap-3 px-4 py-2.5 border rounded-xl cursor-pointer transition-all
                                                            ${selecionado
                                                                ? 'bg-green-50 dark:bg-green-900/15 border-green-300 dark:border-green-800/60'
                                                                : 'bg-white dark:bg-slate-900 border-gray-100 dark:border-slate-700/50 hover:border-green-300/60'}
                                                        `}
                                                    >
                                                        <div className={`mt-0.5 flex-shrink-0 w-4 h-4 rounded-full border flex items-center justify-center transition-colors ${selecionado ? 'border-green-600 bg-green-600' : 'border-gray-300 dark:border-slate-600'}`}>
                                                            {selecionado && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <p className="text-sm font-semibold text-gray-800 dark:text-slate-100 truncate">
                                                                {[end.rua, end.numero, quadraLote, end.complemento].filter(Boolean).join(', ')}
                                                            </p>
                                                            <p className="text-xs text-gray-500 dark:text-slate-400 truncate">
                                                                {[end.bairro, end.cidade && `${end.cidade}/${end.estado}`, end.cep].filter(Boolean).join(' · ')}
                                                            </p>
                                                        </div>
                                                        <span className={`flex-shrink-0 text-[10px] font-bold px-2 py-0.5 rounded-full ${opcao.origem === 'lemit'
                                                            ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                                                            : 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'}`}>
                                                            {opcao.origem === 'lemit'
                                                                ? `Lemit · ${end.fonte === 'cache' ? 'consulta salva' : 'consulta nova'}`
                                                                : `UC ${opcao.ucNumero}`}
                                                        </span>
                                                    </div>
                                                );
                                            })}
                                            {lemitEnderecos.length === 0 && (
                                                <p className="flex items-center gap-1.5 text-xs text-amber-700 dark:text-amber-400 px-1">
                                                    <AlertCircle size={12} className="flex-shrink-0" />
                                                    Titular sem endereço na Lemit — opções acima vêm do cadastro das UCs.
                                                </p>
                                            )}
                                        </div>
                                    ) : (
                                        <div className="px-4 py-3 bg-amber-50 dark:bg-amber-900/15 border border-amber-200/70 dark:border-amber-800/40 rounded-xl">
                                            <p className="flex items-center gap-1.5 text-xs text-amber-800 dark:text-amber-300">
                                                <AlertCircle size={12} className="flex-shrink-0" />
                                                Sem endereço na Lemit e nas UCs — o termo sairá com o endereço da 1ª UC selecionada (se houver).
                                            </p>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Termo: modelo de cobrança (#6367). Desde 03/11/2026 todo cliente novo é boleto único — sem escolha. */}
                            {isTerm && ucsDoTermo.some(ucDaVigor) && (
                                <div className="mb-4">
                                    <span className="flex items-center gap-1.5 text-[10px] font-extrabold uppercase text-gray-500 dark:text-slate-400 mb-2">
                                        <FileText size={12} />
                                        Modelo de cobrança
                                    </span>
                                    <div className="px-3 py-2.5 rounded-xl bg-gray-50 dark:bg-slate-800/60 border border-gray-100 dark:border-slate-700/50">
                                        <p className="text-xs font-bold text-gray-800 dark:text-slate-100">Boleto unificado</p>
                                        <p className="text-[11px] text-gray-600 dark:text-slate-400 mt-0.5">
                                            Um boleto só, com a Vigor e a conta da Equatorial juntas. A assinatura do termo vale como aceite.
                                            Quem tem débito automático na Equatorial só entra depois de cancelá-lo.
                                        </p>
                                    </div>
                                </div>
                            )}

                            {/* List */}
                            <div className="space-y-3 mb-6">
                                {ucs.length === 0 ? (
                                    <div className="text-center py-8 text-gray-400 dark:text-slate-500 border-2 border-dashed border-gray-100 dark:border-slate-700/50 rounded-xl">
                                        Nenhuma UC encontrada para esta negociação.
                                    </div>
                                ) : (
                                    ucs.map(uc => (
                                        <div
                                            key={uc.id}
                                            className={`
                                                relative border rounded-xl p-4 transition-all cursor-pointer flex items-start gap-4 hover:border-primary/30
                                                ${selectedIds.includes(uc.id) ? 'bg-primary/5 border-primary/40' : 'bg-white dark:bg-slate-900 border-gray-100 dark:border-slate-700/50'}
                                            `}
                                            onClick={() => toggleSelect(uc.id)}
                                        >
                                            <div className={`mt-1 flex-shrink-0 w-5 h-5 rounded border flex items-center justify-center transition-colors ${selectedIds.includes(uc.id) ? 'bg-primary border-primary text-white' : 'border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800'}`}>
                                                {selectedIds.includes(uc.id) && <CheckCircle2 size={12} />}
                                            </div>
                                            <div className="flex-1">
                                                <div className="flex justify-between items-start">
                                                    <span className="font-bold text-gray-800 dark:text-slate-100 text-sm">{uc.numero_uc}</span>
                                                    <span className="text-xs font-mono bg-gray-100 dark:bg-slate-800 text-gray-600 dark:text-slate-300 px-2 py-0.5 rounded">{uc.concessionaria || 'N/A'}</span>
                                                </div>
                                                <div className="grid grid-cols-2 gap-x-4 gap-y-1 mt-2 text-sm text-gray-600 dark:text-slate-300">
                                                    <div>
                                                        <span className="text-gray-400 dark:text-slate-500 text-xs uppercase mr-1">Consumo:</span>
                                                        <span className="font-medium">{uc.consumo_medio_kwh?.toLocaleString('pt-BR')} kWh</span>
                                                    </div>
                                                    <div>
                                                        <span className="text-gray-400 dark:text-slate-500 text-xs uppercase mr-1">Tarifa:</span>
                                                        <span className="font-medium">R$ {uc.valor_tarifa?.toLocaleString('pt-BR', { minimumFractionDigits: 4 })}</span>
                                                    </div>
                                                    {(uc.desconto_tarifa || uc.desconto_tarifa === 0) && (
                                                        <div className="col-span-2 flex gap-3 text-xs mt-1">
                                                            <span className="text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20 px-2 py-0.5 rounded">
                                                                Desc. Tarifa: {(uc.desconto_tarifa * 100).toFixed(0)}%
                                                            </span>
                                                            {uc.desconto_bandeira !== undefined && (
                                                                <span className="text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20 px-2 py-0.5 rounded">
                                                                    Desc. Bandeira: {(uc.desconto_bandeira * 100).toFixed(0)}%
                                                                </span>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                                {isTerm && selectedIds.includes(uc.id) && !ucDaVigor(uc) && (
                                                    <p className="mt-3 text-[11px] text-gray-500 dark:text-slate-400">Empresa parceira: cobrança já sai no boleto unificado da parceira.</p>
                                                )}
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>

                            {/* Add Button */}
                            <div className="relative">
                                <button
                                    onClick={() => setShowAddOptions(!showAddOptions)}
                                    className="w-full py-3 bg-primary/10 hover:bg-primary/20 text-primary rounded-xl font-semibold transition-colors flex items-center justify-center gap-2 border border-primary/20 border-dashed"
                                >
                                    <Plus size={18} />
                                    Adicionar nova UC
                                    <ChevronDown size={16} className={`transition-transform ${showAddOptions ? 'rotate-180' : ''}`} />
                                </button>

                                {showAddOptions && (
                                    <div className="absolute top-full left-0 w-full mt-2 bg-white dark:bg-slate-900 border border-gray-100 dark:border-slate-700/50 rounded-xl shadow-xl z-10 overflow-hidden animate-fade-in-up">
                                        <button
                                            onClick={() => {
                                                setIsUnidadeModalOpen(true);
                                                setShowAddOptions(false);
                                            }}
                                            className="w-full text-left px-4 py-3 hover:bg-gray-50 dark:hover:bg-slate-800 flex items-center gap-3 transition-colors"
                                        >
                                            <div className="w-8 h-8 rounded-lg bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 flex items-center justify-center">
                                                <FileText size={16} />
                                            </div>
                                            <div>
                                                <span className="block text-sm font-bold text-gray-800 dark:text-slate-100">Inserir dados manualmente</span>
                                                <span className="block text-xs text-gray-500 dark:text-slate-400">Cadastrar via formulário padrão</span>
                                            </div>
                                        </button>
                                        <button
                                            disabled
                                            className="w-full text-left px-4 py-3 bg-gray-50 dark:bg-slate-800/50 opacity-60 cursor-not-allowed flex items-center gap-3"
                                        >
                                            <div className="w-8 h-8 rounded-lg bg-gray-200 dark:bg-slate-700 text-gray-500 dark:text-slate-400 flex items-center justify-center">
                                                <AlertCircle size={16} />
                                            </div>
                                            <div>
                                                <div className="flex items-center gap-2">
                                                    <span className="block text-sm font-bold text-gray-800 dark:text-slate-100">Enviar fatura em PDF</span>
                                                    <span className="text-[10px] bg-gray-200 dark:bg-slate-700 text-gray-600 dark:text-slate-300 px-1.5 py-0.5 rounded-full font-bold">Em breve</span>
                                                </div>
                                                <span className="block text-xs text-gray-500 dark:text-slate-400">IA extrai os dados automaticamente</span>
                                            </div>
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="p-4 border-t border-gray-100 dark:border-slate-700/50 flex flex-wrap items-center justify-end gap-3 bg-gray-50/50 dark:bg-slate-800/80">
                            {motivoTravado && (
                                <p id="termo-motivo-travado" className="mr-auto text-xs font-semibold text-amber-700 dark:text-amber-400">{motivoTravado}</p>
                            )}
                            <button
                                onClick={onClose}
                                className="px-6 py-2.5 rounded-xl border border-gray-200 dark:border-slate-700 text-gray-600 dark:text-slate-300 font-semibold hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={handleGenerateSimulation}
                                disabled={loading || !!motivoTravado}
                                title={motivoTravado ?? undefined}
                                aria-describedby={motivoTravado ? 'termo-motivo-travado' : undefined}
                                className="px-6 py-2.5 rounded-xl bg-primary text-white font-semibold hover:bg-primary/90 transition-all shadow-lg shadow-primary/30 flex items-center gap-2 disabled:opacity-70 disabled:shadow-none"
                            >
                                {loading ? <Loader2 size={18} className="animate-spin" /> : isTerm ? <FileText size={18} /> : <Calculator size={18} />}
                                {isTerm ? 'Gerar termo' : 'Gerar simulação'}
                            </button>
                        </div >
                    </>
                )}
            </div >

            {/* Nested Modal */}
            < UnidadeModal
                isOpen={isUnidadeModalOpen}
                onClose={() => setIsUnidadeModalOpen(false)}
                initialNegotiation={{ id: Number(negotiationId), cliente_nome: clientName }}
                onSave={() => {
                    if (onUpdate) onUpdate(); // We can call it, generally parent refreshes everything
                    // Ideally we should NOT call onUpdate because it will close the simulation modal if logic elsewhere triggers?
                    // No, onUpdate just refreshes data. 
                }}
            />
        </div >,
        document.body
    );
};

export default SimulationModal;
